import { MultiSelect } from '@predoc/cow';
import {
  useGetOrganizationFacesheets,
  useGetOrganizationProfileFacesheets,
} from '../hooks/facesheetsAPI';
import { Facesheet } from '../types';
import { getFacesheetPillText } from '../utils/facesheets';

interface FacesheetSelectorProps {
  profileId?: string;
  values: Facesheet[];
  onChange: (values: Facesheet[]) => void;
  placeholder?: string;
  maxVisible?: number;
  className?: string;
  chipPosition?: 'inline' | 'below';
  disabled?: boolean;
  filterFacesheet?: (facesheet: Facesheet) => boolean;
}

const facesheetToOption = (facesheet: Facesheet) => {
  const pillText = getFacesheetPillText(facesheet);
  return {
    value: facesheet.facesheetConfigId,
    label: facesheet.displayName,
    // `shortcut` is required by @predoc/cow MultiSelect option shape.
    shortcut: pillText,
  };
};

export const FacesheetSelector = ({
  profileId,
  values,
  onChange,
  placeholder = 'Select facesheets...',
  maxVisible = 2,
  className = 'w-full',
  chipPosition = 'below',
  disabled = false,
  filterFacesheet,
}: FacesheetSelectorProps) => {
  const shouldFetchProfileFacesheets = !!profileId;
  const {
    data: profileFacesheets,
    isLoading: isProfileLoading,
    error: profileError,
  } = useGetOrganizationProfileFacesheets(profileId);

  const {
    data: organizationFacesheets,
    isLoading: isOrganizationFacesheetsLoading,
    error: organizationFacesheetsError,
  } = useGetOrganizationFacesheets({
    orgWideOnly: true,
    enabled: !shouldFetchProfileFacesheets,
  });

  const allFacesheets = shouldFetchProfileFacesheets
    ? (profileFacesheets ?? [])
    : (organizationFacesheets?.facesheets ?? []);

  const filteredFacesheets = filterFacesheet
    ? allFacesheets.filter(filterFacesheet)
    : allFacesheets;

  const options = filteredFacesheets.map(facesheetToOption);
  const selectedIds = values.map((facesheet) => facesheet.facesheetConfigId);

  // Handle change by converting IDs back to facesheet objects
  const handleChange = (selectedIds: string[]) => {
    const selectedFacesheets = selectedIds
      .map((id) =>
        filteredFacesheets.find(
          (facesheet) => facesheet.facesheetConfigId === id,
        ),
      )
      .filter(Boolean) as Facesheet[];

    onChange(selectedFacesheets);
  };

  const showError = shouldFetchProfileFacesheets
    ? profileError
    : organizationFacesheetsError;
  const isLoading = shouldFetchProfileFacesheets
    ? isProfileLoading
    : isOrganizationFacesheetsLoading;

  if (showError) {
    return (
      <div className="text-sm text-red-600">
        Error loading facesheets. Please try again.
      </div>
    );
  }

  return (
    <MultiSelect
      options={options}
      values={selectedIds}
      handleChange={handleChange}
      placeholder={isLoading ? 'Loading facesheets...' : placeholder}
      maxVisible={maxVisible}
      className={className}
      chipPosition={chipPosition}
      disabled={disabled || isLoading}
    />
  );
};
